import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ICompanyResponse, IGETTakeoverUrlResponse } from '@cbgms/api'
import { generateSubmissionError } from 'utils-support/Form/SubmissionError'
import {
  getShop,
  getShopLocalizationSettings,
  getTakeoverURL,
  listShops,
  postShopToCRM,
  resetShop,
  updateShop,
  updateShopLocalizationSettings
} from 'app-support/shops/state/actions'
import { getCompany, resetCompany } from 'app-support/company/state/actions'
import ShopForm, { IFormValues } from './ShopForm'
import { GETConceptsResponse, GETShopConceptsResponse, IShopConcept } from '@cbgms/api/modules/concepts'
import { createShopConcept, listConcepts, listShopConcepts, updateShopConcept } from 'app-support/concepts/state/actions'
import { GETLocalisationSettingsResponse } from '@cbgms/api/modules/settings'
import useQuery, { useQueryClient } from '@cbgms/base/http/use-query'
import { useGetShopExternalGMSData } from '../hooks/use-get-shop-externalgms-data'
import { IRootReducerState } from 'state/reducers'
import { useNotifier } from '@cbgms/base/notifier'
import CustomTypography from 'app-support/CustomTypography'
import Stack from '@mui/material/Stack'
import Divider from '@mui/material/Divider'
import { ConfirmDialogPromise } from '@carsys/ui'
import { ShopListRow } from './List'
import { query } from '@cbgms/components/list'

export const FORM_NAME = 'shop_edit'

const EditShop: React.FC<{}> = () => {
  const { companyUUID, shopID } = useParams<{ companyUUID: string; shopID: string }>()
  const redirectToURL = `/companies/edit/${companyUUID}`
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const notifier = useNotifier()
  // functions passed as props
  const updateShopD = useCallback((...input: Parameters<typeof updateShop>) => dispatch(updateShop(...input)), [dispatch])
  const updateShopLocalizationSettingsD = useCallback(
    (...input: Parameters<typeof updateShopLocalizationSettings>) => dispatch(updateShopLocalizationSettings(...input)),
    [dispatch]
  )
  const postShopToCRMD = useCallback((...input: Parameters<typeof postShopToCRM>) => dispatch(postShopToCRM(...input)), [dispatch])
  const createShopConceptD = useCallback(
    (...input: Parameters<typeof createShopConcept>) => dispatch(createShopConcept(...input)),
    [dispatch]
  )
  const updateShopConceptD = useCallback(
    (...input: Parameters<typeof updateShopConcept>) => dispatch(updateShopConcept(...input)),
    [dispatch]
  )
  const { isLoading: isLoadingExternalGMS, data: externalGMS } = useGetShopExternalGMSData(shopID)
  const postShopToCRMDateTime = useSelector<IRootReducerState>(state => state.shop.postShopToCRMDateTime)
  const companyName = useSelector<IRootReducerState, string>(state => state.company.Name)
  const shopName = useSelector<IRootReducerState>(state => state.shop.Name)

  const queryClient = useQueryClient()
  const { data: result, isLoading: shopIsLoading } = useQuery(
    [FORM_NAME, 'RetrieveAllData', companyUUID, shopID, postShopToCRMDateTime],
    async () => {
      const { Data: shop } = await dispatch(getShop(shopID))

      const [, conceptsResponse, shopConceptsResponse, takeoverResponse, shopLocalizationSettingsResponse] = await Promise.all([
        dispatch(getCompany(companyUUID) as any as ICompanyResponse),
        dispatch(listConcepts({ fields: 'Concept.UUID, Concept.Name' }) as any as GETConceptsResponse),
        dispatch(listShopConcepts(shop.UUID, { fields: '*' }) as any as GETShopConceptsResponse),
        dispatch(getTakeoverURL(shop.ID) as any as IGETTakeoverUrlResponse),
        dispatch(getShopLocalizationSettings(shop.ID) as any as GETLocalisationSettingsResponse)
      ])

      return {
        concepts: conceptsResponse.Data.map(concept => concept.Concept),
        shopConcepts: shopConceptsResponse.Data.map(shopConcept => shopConcept.ShopInConcept),
        takeoverURL: takeoverResponse.Data,
        shopLocalizationSettings: shopLocalizationSettingsResponse.Data,
        shop
      }
    }
  )

  const onSubmit = async (values: IFormValues) => {
    const VisitorLongitude = parseFloat(values?.Shop?.VisitorLongitude?.toString())
    const VisitorLatitude = parseFloat(values?.Shop?.VisitorLatitude?.toString())
    const updateShopBody = {
      ...values?.Shop,
      VisitorLongitude: Number.isNaN(VisitorLongitude) ? undefined : VisitorLongitude,
      VisitorLatitude: Number.isNaN(VisitorLatitude) ? undefined : VisitorLatitude
    }
    const updateShopLocalizationSettingsBody = {
      ...values.ShopLocalizationSettings,
      DateNotation: undefined // don't want to set DateNotation, it's stored on the backend but not configurable by user
    }

    try {
      const search = query<ShopListRow>()
        .contains(i => i.Shop.PhoneNumber, values.Shop.PhoneNumber)
        .and()
        .notEquals(i => i.Shop.ID, shopID)
        .toString()

      const duplicates = await dispatch(
        listShops({
          fields: 'Shop.*',
          search
        })
      )
      if (duplicates.Data.length) {
        await ConfirmDialogPromise({
          content: (
            <div>
              {t('Auth.OnboardingDuplicateOnPhoneNumber')}
              <ul>
                {duplicates.Data.map(shop => (
                  <li key={shop.Shop.ID}>
                    <a href={`/companies/${shop.Shop.CompanyUUID}/shops/edit/${shop.Shop.ID}`}>
                      {shop.Shop.Name} ({shop.Shop.ID})
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ),
          confirmButtonContent: t('global.confirm'),
          cancelButtonContent: t('global.cancel')
        })
      }
      await updateShopD(shopID, updateShopBody)
      await updateShopLocalizationSettingsD(shopID, updateShopLocalizationSettingsBody)
    } catch (error) {
      throw generateSubmissionError(error)
    }
  }

  const onSubmitAndDone = async (values: IFormValues) => {
    try {
      await onSubmit(values)
      return redirectToURL
    } catch (error) {
      throw error
    }
  }

  const onConceptSubmit =
    (conceptUUID: string) =>
    async (values: IFormValues, _: any, { result }: any) => {
      const shopConcept = values.ShopConcepts.find((sc: IShopConcept) => sc.ConceptUUID === conceptUUID)
      try {
        await updateShopConceptD(result!.shop.UUID, conceptUUID, shopConcept!)
      } catch (error) {
        throw generateSubmissionError(error)
      }
    }

  const onConceptAdd =
    (conceptUUID: string) =>
    async (__: IFormValues, _: any, { result }: any) => {
      try {
        await createShopConceptD(result!.shop.UUID, {
          ShopUUID: result!.shop.UUID,
          CompanyUUID: companyUUID,
          ConceptUUID: conceptUUID,
          IsEnabled: false
        })
      } catch (error) {
        throw generateSubmissionError(error)
      }
    }

  const onSubmitSuccess = () => {
    notifier.success(t('shop.updated_successfully'))
  }

  const onAddShopToCRM = async (shopID: string, postShopToCRM: (id: string) => Promise<{}>) => {
    try {
      await postShopToCRM(shopID)
      notifier.success(t('Shop.AddToCRMSuccess'))
    } catch {
      notifier.error(t('Shop.AddToCRMFail'))
    }
  }

  const isLoading = shopIsLoading || isLoadingExternalGMS
  const additionalProps = useMemo(
    () => ({
      form: FORM_NAME,
      onCancel: redirectToURL,
      initialValues: {
        Shop: result?.shop,
        ShopConcepts: result?.shopConcepts,
        ShopLocalizationSettings: result?.shopLocalizationSettings,
        ExternalGMS: { Name: externalGMS?.Remark || externalGMS?.Name || '' }
      },
      onSubmit,
      onSubmitAndDone,
      onSubmitSuccess: () => {
        queryClient.invalidateQueries([FORM_NAME])
        return onSubmitSuccess()
      },
      onConceptSubmit,
      onConceptAdd,
      onAddShopToCRM: (shopID: string) => onAddShopToCRM(shopID, postShopToCRMD)
    }),
    [result, isLoading, postShopToCRMD]
  )

  useEffect(() => {
    return () => {
      resetCompany()
      resetShop()
      queryClient.invalidateQueries([FORM_NAME])
    }
  }, [])

  if (isLoading) {
    return null
  }

  return (
    <>
      <Stack direction='row' divider={<Divider orientation='vertical' />} spacing={0.5}>
        <Link to={redirectToURL}>
          <CustomTypography color='primary' text={companyName} />
        </Link>
        /
        <Stack direction='row' spacing={0.5}>
          <CustomTypography text={`${shopName}`} fontWeight='bold' />
          <CustomTypography text={`(${shopID})`} fontWeight='bold' />
        </Stack>
      </Stack>
      <Stack direction='row' spacing={0.5} py={1}>
        <CustomTypography text={`${shopName}`} level='h5' fontWeight='bold' />
        <CustomTypography text={`(${shopID})`} level='h5' fontWeight='bold' />
      </Stack>

      <div className='shopForm'>
        <ShopForm
          {...additionalProps}
          result={result}
          form={FORM_NAME}
          updateShop={updateShopD}
          updateShopLocalizationSettings={updateShopLocalizationSettingsD}
          postShopToCRM={postShopToCRMD}
          createShopConcept={createShopConceptD}
          updateShopConcept={updateShopConceptD}
        />
      </div>
    </>
  )
}

export default EditShop
